/**
 * Engine Editor ownership-cascading composition shell — slide-in panel from
 * right per scope §5.5.1 amended + Sol direction at Day 2.5 P0 close (Polaris-
 * inspired admin-stance edit surface for engine-cascading + ownership +
 * reversible-with-residue fields).
 *
 * Day 2.5 P1 scope: wires three bswc fields per scope §5.5.1 amended V1 4a
 * field list (is_available, applies_to_metric, penalty). NOT brands.enforces_map
 * (Day 3 — column-surgical extraction per scope §3.7).
 *
 * Day 2.5 P3 F33 dynamic gating overlay: branch detection on the is_available ×
 * applies_to_metric pair per §6.1 + §6.3. Four branches A/B/C/D + standard
 * (penalty + all non-pre-coherent edits). Atomic-unit branches A+B render
 * multi-column commit envelope (both columns edited together). Trap-resolving
 * branches C+D render single-column commit with resolution acknowledgment.
 *
 * Slide-in pattern mirrors arbi-root HelpOverlay precedent (fixed top-[52px]
 * right-0 w-96 h-[calc(100vh-52px-36px)] bg-surface-1 border-l shadow-lg).
 * Esc-dismiss mirrors ConfirmDialog.tsx:39-46 pattern.
 *
 * Six-component affordance shape per §5.5.1 amended (composed across L5+L6+L9
 * + CF 68 framing):
 *   1. Confirmation prompt — substrate-shape-honest copy varies per field +
 *      F33 branch overlay
 *   2. Annotation slot — admin records rationale; surfaces in L7 trace
 *   3. Propagation preview — surfaces L8 curated map content verbatim per
 *      Atlas §6.4 L8 inheritance
 *   4. Notification per CF 68 — downstream consumer notification preview
 *   5. Forward-only reversibility framing at save (behavior-as-preservation
 *      copy per §3.3 sub-corollary)
 *   6. Residue visibility affordance — scaffolded link per Atlas §4.7 inspect-
 *      altitude pattern (full residue inspection at V2 promotion build day)
 */

import { useEffect, useState, type JSX } from 'react';
import { X } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import {
  detectF33Branch,
  type BswcRowState,
  type F33Branch,
  type OwnershipCascadingField,
} from '../../../lib/engine-editor/f33Gating';

// Re-export types for downstream consumers that imported from the panel
// pre-Day-3-P0.5 (preserve import-surface backwards compatibility while the
// extracted lib lives at lib/engine-editor/f33Gating).
/** @public — root-tests-import-apps (U11 census C1): BswcRowState is consumed by the root suite. */
export type { BswcRowState, OwnershipCascadingField } from '../../../lib/engine-editor/f33Gating';

export interface EngineEditorOwnershipCascadingPanelProps {
  row: BswcRowState;
  field: OwnershipCascadingField;
  onSaved: () => void;
  onClose: () => void;
}

// Per-field labels for headers + prompt copy.
const FIELD_LABELS: Record<OwnershipCascadingField, string> = {
  is_available: 'Available',
  applies_to_metric: 'Metric',
  penalty: 'Penalty',
};

// Confirmation prompt copy per field (substrate-shape-honest framing per §5.5.1).
function standardPromptCopy(field: OwnershipCascadingField): string {
  switch (field) {
    case 'is_available':
      return 'Toggle whether this Buy Score component participates in engine routing. This is a stance change — past calculations preserve prior availability via param_snapshot.';
    case 'applies_to_metric':
      return 'Change which engine metric this Buy Score component reads from. Stance change affecting engine routing — past calculations preserve prior metric mapping.';
    case 'penalty':
      return 'Flip this component’s contribution direction (penalty=true negates contribution at engine CASE arm). Stance change affecting engine math sign — past calculations preserve prior penalty direction.';
  }
}

// F33 branch-overlay prompt copy per §6.1 + §6.3.
function f33PromptCopy(branch: F33Branch): string | null {
  switch (branch) {
    case 'A':
    case 'B':
      return 'This Buy Score component requires both an availability toggle and a metric assignment to participate in the engine blend. Edit both together to enable the component.';
    case 'C':
      return 'This edit resolves the silent-zero risk on this row. Setting applies_to_metric enables the engine to apply this component.';
    case 'D':
      return 'This edit resolves the silent-zero risk on this row by disabling the component.';
    case 'standard':
      return null;
  }
}

export default function EngineEditorOwnershipCascadingPanel({
  row,
  field,
  onSaved,
  onClose,
}: EngineEditorOwnershipCascadingPanelProps): JSX.Element {
  // Proposed value state — initialized from current row state for the field.
  const [proposedIsAvailable, setProposedIsAvailable] = useState<boolean>(row.is_available);
  const [proposedAppliesToMetric, setProposedAppliesToMetric] = useState<string | null>(
    row.applies_to_metric,
  );
  const [proposedPenalty, setProposedPenalty] = useState<boolean>(row.penalty);
  // DEV-F3 (Fix Pass A) — L7-trace annotation is V2-scope (ADR-39 / scope-doc);
  // handleSave never persisted it. Disabled coming-soon affordance below — NO state.
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Esc-dismiss per ConfirmDialog.tsx:39-46 pattern.
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape' && !saving) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [saving, onClose]);

  // F33 branch detection — recomputed on every render based on current proposed values.
  const branch = detectF33Branch(row, field, proposedIsAvailable, proposedAppliesToMetric);
  const overlayPrompt = f33PromptCopy(branch);
  const isAtomicUnit = branch === 'A' || branch === 'B';

  const handleSave = async (): Promise<void> => {
    setSaving(true);
    setError(null);

    // Atomic-unit branches A/B commit both is_available + applies_to_metric in
    // single statement. Single-column branches (standard + C + D) commit just
    // the edited field.
    let updates: Partial<BswcRowState>;
    if (isAtomicUnit) {
      updates = {
        is_available: proposedIsAvailable,
        applies_to_metric: proposedAppliesToMetric,
      };
    } else if (field === 'is_available') {
      updates = { is_available: proposedIsAvailable };
    } else if (field === 'applies_to_metric') {
      updates = { applies_to_metric: proposedAppliesToMetric };
    } else {
      updates = { penalty: proposedPenalty };
    }

    const { error: updateError } = await supabase
      .from('buy_score_weight_components')
      .update(updates)
      .eq('id', row.id);

    if (updateError) {
      setError(updateError.message);
      setSaving(false);
      return;
    }

    setSaving(false);
    onSaved();
  };

  return (
    <>
      {/* Backdrop overlay — click outside to dismiss (unless saving) */}
      <div
        className="fixed inset-0 bg-black/30 z-[calc(var(--z-modal,200))]"
        onClick={() => !saving && onClose()}
        aria-hidden="true"
      />

      {/* Slide-in panel from right — mirrors HelpOverlay positioning + ParallelCalcDebugPanel z-index */}
      <aside
        role="dialog"
        aria-label={`Edit ${FIELD_LABELS[field]} for ${row.key}`}
        className="fixed top-0 right-0 w-96 h-full z-[calc(var(--z-modal,200)+1)] flex flex-col bg-surface-1 border-l border-border shadow-2xl"
      >
        {/* Header */}
        <header className="flex items-start justify-between gap-2 p-4 border-b border-border">
          <div>
            <h2 className="text-sm font-semibold text-ink-bright">
              Edit {FIELD_LABELS[field]}
            </h2>
            <p className="text-xs text-ink-muted mt-0.5 font-mono">{row.key}</p>
          </div>
          <button
            type="button"
            onClick={() => !saving && onClose()}
            disabled={saving}
            className="text-ink-muted hover:text-ink-primary disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
            aria-label="Close edit panel"
          >
            <X size={16} />
          </button>
        </header>

        {/* Body (scrollable) */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* (1) Confirmation prompt — F33 overlay if applicable, else standard */}
          <div>
            <p className="text-xs text-ink-secondary leading-relaxed">
              {overlayPrompt ?? standardPromptCopy(field)}
            </p>
          </div>

          {/* New value input(s) — atomic-unit branches render BOTH columns */}
          <div className="space-y-3">
            {(isAtomicUnit || field === 'is_available') && (
              <div>
                <label
                  htmlFor="oc-panel-is-available"
                  className="block text-xs font-semibold text-ink-bright mb-1"
                >
                  Available
                </label>
                <div className="flex items-center gap-2">
                  <input
                    id="oc-panel-is-available"
                    type="checkbox"
                    checked={proposedIsAvailable}
                    onChange={(e) => setProposedIsAvailable(e.target.checked)}
                    disabled={saving}
                    className="cursor-pointer"
                  />
                  <span className="text-xs text-ink-secondary">
                    {proposedIsAvailable ? 'true' : 'false'}
                  </span>
                </div>
              </div>
            )}
            {(isAtomicUnit || field === 'applies_to_metric') && (
              <div>
                <label
                  htmlFor="oc-panel-applies-to-metric"
                  className="block text-xs font-semibold text-ink-bright mb-1"
                >
                  Metric (applies_to_metric)
                </label>
                <input
                  id="oc-panel-applies-to-metric"
                  type="text"
                  value={proposedAppliesToMetric ?? ''}
                  onChange={(e) =>
                    setProposedAppliesToMetric(e.target.value === '' ? null : e.target.value)
                  }
                  disabled={saving}
                  placeholder="(NULL — engine ignores this row)"
                  className="w-full px-2 py-1 text-xs bg-surface-2 border border-border rounded text-ink-primary focus:border-accent focus:outline-none"
                />
              </div>
            )}
            {field === 'penalty' && (
              <div>
                <label
                  htmlFor="oc-panel-penalty"
                  className="block text-xs font-semibold text-ink-bright mb-1"
                >
                  Penalty (negates contribution)
                </label>
                <div className="flex items-center gap-2">
                  <input
                    id="oc-panel-penalty"
                    type="checkbox"
                    checked={proposedPenalty}
                    onChange={(e) => setProposedPenalty(e.target.checked)}
                    disabled={saving}
                    className="cursor-pointer"
                  />
                  <span className="text-xs text-ink-secondary">
                    {proposedPenalty ? 'true' : 'false'}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* (2) Annotation slot — DEV-F3 coming-soon (V2; not persisted) */}
          <div>
            <label
              htmlFor="oc-panel-annotation"
              className="block text-xs font-semibold text-ink-bright mb-1"
            >
              Rationale (coming soon)
            </label>
            <textarea
              id="oc-panel-annotation"
              disabled
              rows={2}
              placeholder="L7-trace annotation capture is coming in a post-launch release — not saved yet."
              className="w-full px-2 py-1 text-xs bg-surface-2 border border-border rounded text-ink-muted resize-y opacity-60 cursor-not-allowed"
            />
            <p className="mt-1 text-2xs text-ink-muted">Not saved yet — coming post-launch.</p>
          </div>

          {/* (3) Propagation preview per Atlas §6.4 L8 inheritance */}
          <div className="bg-surface-2 border border-border-subtle rounded p-3 space-y-1">
            <h3 className="text-xs font-semibold text-ink-bright">Affects on commit</h3>
            <p className="text-xs text-ink-secondary leading-relaxed">
              BuyingSession headers via bswcTooltipFallback (Day 91a.5 P2D),
              ParamsSidebar auto-rebalance (Day 91a P2B), product_profitability cache.
            </p>
          </div>

          {/* (4) Notification per CF 68 */}
          <div>
            <p className="text-xs text-ink-muted italic">
              Notifies: downstream consumers on commit per CF 68.
            </p>
          </div>

          {/* (5) Forward-only reversibility framing per §3.3 sub-corollary */}
          <div>
            <p className="text-xs text-ink-muted italic">
              This edit takes effect for future engine outputs. Past calculations preserve
              prior values via param_snapshot.
            </p>
          </div>

          {/* (6) Residue visibility affordance per Atlas §4.7 inspect-altitude pattern */}
          <div>
            <button
              type="button"
              onClick={() => {
                // V1 scaffolding per Atlas §4.7 — full residue inspection at V2
                // promotion build day. Placeholder no-op surface for admin-trust signal.
                // eslint-disable-next-line no-console
                console.info(
                  '[engine-editor] residue visibility wires at V2 promotion build day',
                );
              }}
              disabled={saving}
              className="text-xs text-accent hover:underline cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
            >
              View past calculations affected by prior value →
            </button>
          </div>

          {error && (
            <div className="bg-red-500/10 border border-red-500/30 rounded p-2">
              <p className="text-xs text-red-500">Save failed: {error}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <footer className="flex items-center justify-end gap-2 p-4 border-t border-border">
          <button
            type="button"
            onClick={() => !saving && onClose()}
            disabled={saving}
            className="px-3 py-1.5 text-xs text-ink-secondary hover:text-ink-primary disabled:opacity-40 cursor-pointer disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="px-3 py-1.5 text-xs bg-accent text-surface-0 rounded hover:bg-accent/80 disabled:opacity-40 cursor-pointer disabled:cursor-not-allowed"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </footer>
      </aside>
    </>
  );
}
